import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles/ReviewsPage.css';
import SpotlightCard from '../components/SpotlightCard';
import ButtonInterceptor from '../components/ButtonInterceptor';
import CongratulatoryNotification from '../components/CongratulatoryNotification';
import {
  getAllReviews,
  addReview,
  Review as DynamoDBReview
} from '../services/dynamoDBService';
import { incrementPositiveReviewsCount, isAmongFirstPositiveReviews } from '../services/reviewCounterService';

// Use the Review interface from the DynamoDB service
type Review = DynamoDBReview;

const ReviewsPage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    rating: 0,
    content: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [reviewRejected, setReviewRejected] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showCongratulations, setShowCongratulations] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleRatingClick = (rating: number) => {
    setFormData(prevState => ({
      ...prevState,
      rating
    }));
  };

  // Fetch reviews from DynamoDB when component mounts
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const reviewsData = await getAllReviews();
        // Note: getAllReviews already sorts and limits to top 5 reviews,
        // but we'll sort again here to ensure consistency with our local sorting
        const sortedReviews = sortReviews(reviewsData);
        setReviews(sortedReviews);
        setError(null);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        // The getAllReviews function now handles errors internally and returns sample data,
        // so this catch block should rarely be executed
        setError('Failed to load reviews. Using sample data instead.');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset validation errors
    setValidationError(null);
    
    // Validate form fields with maxLength constraints
    if (!formData.name.trim()) {
      setValidationError("Please enter your name");
      return;
    }
    
    if (formData.name.trim().length > 100) {
      setValidationError("Name must be 100 characters or less");
      return;
    }
    
    if (formData.rating === 0) {
      setValidationError("Please select a rating");
      return;
    }
    
    if (!formData.content.trim()) {
      setValidationError("Please enter your review");
      return;
    }
    
    if (formData.content.trim().length > 2000) {
      setValidationError("Review must be 2000 characters or less");
      return;
    }
    
    try {
      // Format the date
      const formattedDate = new Date().toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Create a new review object
      const reviewData = {
        name: formData.name.trim(),
        date: formattedDate,
        rating: formData.rating,
        content: formData.content.trim()
      };
      
      // Add the review to DynamoDB (or get back the review object if DynamoDB is not available)
      const result = await addReview(reviewData);
      
      // Check if the review is positive and should receive a congratulatory message
      if (result.isPositive === true) {
        // Increment the positive reviews counter
        await incrementPositiveReviewsCount();
        
        // Check if this review is among the first 50 positive reviews
        const isAmongFirst = await isAmongFirstPositiveReviews();
        if (isAmongFirst) {
          setShowCongratulations(true);
        }
      }
      
      // For all reviews, regardless of sentiment or rating:
      
      // If the review meets our display criteria (positive sentiment and 3+ stars),
      // add it to the displayed reviews list and resort
      if (result.isPositive === true && result.rating >= 3) {
        // Add the new review to the list
        const updatedReviews = [...reviews, result];
        
        // Sort the reviews using the same logic as in dynamoDBService
        const sortedReviews = sortReviews(updatedReviews);
        
        // Limit to top 5
        const limitedReviews = sortedReviews.slice(0, 6);
        
        // Update state with sorted and limited reviews
        setReviews(limitedReviews);
      }
      // Note: Reviews that don't meet criteria are still saved in DynamoDB
      // but won't be displayed in the reviews list
      
      // Reset form for all reviews
      setFormData({
        name: '',
        rating: 0,
        content: ''
      });
      
      // Show success message for all reviews
      setFormSubmitted(true);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setFormSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again later.');
      
      // Even if there's an error, we can still add the review to the local state
      // so the user sees their review (it just won't be saved to DynamoDB)
      const formattedDate = new Date().toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      const newReview: Review = {
        id: Date.now().toString(),
        name: formData.name,
        date: formattedDate,
        rating: formData.rating,
        content: formData.content
      };
      
      setReviews([newReview, ...reviews]);
      
      // Reset form
      setFormData({
        name: '',
        rating: 0,
        content: ''
      });
    }
  };

  // Function to sort reviews by rating and sentiment
  const sortReviews = (reviewsToSort: Review[]): Review[] => {
    return [...reviewsToSort].sort((a, b) => {
      // First sort by rating (highest first)
      if (b.rating !== a.rating) {
        return b.rating - a.rating;
      }
      
      // For equal ratings, prioritize by sentiment type
      // POSITIVE sentiment gets higher priority than MIXED
      if (a.sentiment === 'POSITIVE' && b.sentiment !== 'POSITIVE') {
        return -1;
      }
      if (b.sentiment === 'POSITIVE' && a.sentiment !== 'POSITIVE') {
        return 1;
      }
      
      // If everything is equal, sort by date (newest first)
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  };
  
  // Function to render stars based on rating
  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} style={{ color: i <= rating ? '#f39c12' : '#ccc' }}>
          ★
        </span>
      );
    }
    return stars;
  };

  // Function to get initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  console.log('[DEBUG ReviewsPage] Rendering with SEO Helmet tags');

  return (
    <div className="reviews-page">
      <Helmet>
        <title>Customer Reviews | SpitFire Braai Catering Testimonials</title>
        <meta name="description" content="Read what our customers say about SpitFire Braai Catering. Real reviews from events, functions, and celebrations across South Africa." />
        <meta property="og:title" content="Customer Reviews | SpitFire Braai Catering Testimonials" />
        <meta property="og:description" content="Read what our customers say about SpitFire Braai Catering." />
        <meta property="og:url" content="https://www.spitfirebraai.co.za/reviews" />
        <link rel="canonical" href="https://www.spitfirebraai.co.za/reviews" />
      </Helmet>
      <div className="container">
        <header className="reviews-header">
          <h1>Customer Reviews</h1>
          <p>See what our customers have to say about their SpitFire experience. We pride ourselves on delivering exceptional food and service for every event.</p>
        </header>

        {loading ? (
          <div className="loading-message">Loading reviews...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : reviews.length === 0 ? (
          <div className="no-reviews-message">No reviews yet. Be the first to share your experience!</div>
        ) : (
          <div className="reviews-container">
          {reviews.map((review) => (
            <SpotlightCard
              key={review.id}
              className="review-card"
              spotlightColor="rgba(230, 126, 34, 0.2)"
            >
              <div className="review-header">
                <div className="review-avatar">
                  {getInitials(review.name)}
                </div>
                <div className="review-info">
                  <h3>{review.name}</h3>
                  <div className="review-date">{review.date}</div>
                  <div className="review-rating">
                    {renderStars(review.rating)}
                  </div>
                </div>
              </div>
              <div className="review-content">
                {review.content}
              </div>
            </SpotlightCard>
          ))}
          </div>
        )}

        <div className="review-form-container">
          <h2>Share Your Experience</h2>
          
          {formSubmitted && (
            <div className="form-success">
              <h3>Thank you for your review!</h3>
              <p>Your feedback helps us improve and helps others learn about our services.</p>
            </div>
          )}
          
          {reviewRejected && (
            <div className="form-error">
              <h3>Review Not Accepted</h3>
              <p>{rejectionReason}</p>
              <p>Please consider revising your review or contacting us directly with any concerns.</p>
            </div>
          )}
          
          {error && (
            <div className="form-note">
              <p>Note: {error}</p>
            </div>
          )}
          
          {validationError && (
            <div className="form-error">
              <p>{validationError}</p>
            </div>
          )}
          
          <form className="review-form">
            <div className="form-group">
              <label htmlFor="name">Your Name</label>
              <input
                type="text"
                id="name"
                name="name"
                maxLength={100}
                value={formData.name}
                onChange={handleChange}
                required
              />
              {formData.name.length > 0 && (
                <small>{formData.name.length}/100 characters</small>
              )}
            </div>
            
            <div className="form-group">
              <label>Rating</label>
              <div className="rating-select">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`rating-option ${formData.rating >= star ? 'selected' : ''}`}
                    onClick={() => handleRatingClick(star)}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="content">Your Review</label>
              <textarea
                id="content"
                name="content"
                rows={5}
                maxLength={2000}
                value={formData.content}
                onChange={handleChange}
                required
              ></textarea>
              {formData.content.length > 0 && (
                <small>{formData.content.length}/2000 characters</small>
              )}
            </div>
            
            <ButtonInterceptor
              type="button"
              className="submit-button"
              to="/reviews"
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(e as unknown as React.FormEvent);
              }}
              triggerTransition={() => {
                // Only trigger the transition if the form was submitted successfully
                return formSubmitted;
              }}
            >
              Submit Review
            </ButtonInterceptor>
          </form>
        </div>
      </div>
      
      {showCongratulations && (
        <CongratulatoryNotification
          onClose={() => setShowCongratulations(false)}
        />
      )}
    </div>
  );
};

export default ReviewsPage;
